import type { AccessControlProvider, CanParams, CanReturnType } from '@refinedev/core';
import { authProvider, type UserIdentity } from './authProvider';
import { hasPermission } from '../utils/permissions';
import {
  permissionsCache,
  clearPermissionsCache,
  PERMISSIONS_CACHE_TTL_MS,
} from './permissionsCache';

/**
 * Loads the current user's permissions into the short-lived cache,
 * deduplicating concurrent calls by reusing the in-flight fetch Promise so
 * multiple simultaneous useCan() checks only trigger one /auth/me request.
 * On error, resets all cache state so the next call retries cleanly.
 */
async function loadCachedIdentity(): Promise<string[]> {
  const now = Date.now();
  if (permissionsCache.permissions !== null && now < permissionsCache.expiresAt) {
    return permissionsCache.permissions;
  }
  if (permissionsCache.inflight) {
    return permissionsCache.inflight;
  }
  permissionsCache.inflight = (async () => {
    try {
      const identity = authProvider.getIdentity
        ? ((await authProvider.getIdentity()) as UserIdentity | null)
        : null;
      permissionsCache.permissions = identity?.permissions ?? [];
      permissionsCache.expiresAt = Date.now() + PERMISSIONS_CACHE_TTL_MS;
      return permissionsCache.permissions;
    } catch {
      // On error reset cache so the next call retries rather than
      // serving a permanently poisoned or stale result.
      clearPermissionsCache();
      return [];
    } finally {
      permissionsCache.inflight = null;
    }
  })();
  return permissionsCache.inflight;
}

/**
 * Returns the current user's permissions, using the short-lived identity cache.
 * @returns Array of permission strings held by the current user
 */
async function getCachedPermissions(): Promise<string[]> {
  return loadCachedIdentity();
}

/**
 * The permission an action needs; a list means any one of them (SF-4746: Demos reads take
 * either the create or the read-all permission, like the sf-api any-of gate).
 */
type RequiredPermission = string | string[] | null;

const PERMISSION_MAP: Record<string, Record<string, RequiredPermission>> = {
  dashboard: { list: null, show: null, create: null, edit: null, delete: null },
  companies: {
    list: 'admin:companies:read',
    show: 'admin:companies:read',
    create: 'admin:companies:write',
    edit: 'admin:companies:write',
    delete: 'admin:companies:*',
  },
  users: {
    list: 'admin:users:read',
    show: 'admin:users:read',
    create: 'admin:users:write',
    edit: 'admin:users:write',
    delete: 'admin:users:*',
  },
  // SF-5024: customer:roles:* also opens the Roles tab. The auth service then shows and
  // lets them change customer-scoped roles only. Deleting a role stays admin only.
  // SF-5019: a role's change log is admin only (GET /roles/:id/history needs admin:roles:read),
  // because a role's past can name internal permissions or an internal name it once had.
  roles: {
    list: ['admin:roles:read', 'customer:roles:read'],
    history: 'admin:roles:read',
    show: ['admin:roles:read', 'customer:roles:read'],
    create: ['admin:roles:write', 'customer:roles:write'],
    edit: ['admin:roles:write', 'customer:roles:write'],
    delete: 'admin:roles:*',
  },
  permissions: {
    list: 'permissions:read',
    show: 'permissions:read',
    create: 'permissions:write',
    edit: 'permissions:write',
    delete: 'permissions:*',
  },
  // SF-3180 — SSO Configuration tab on the company detail page. Gated
  // by dedicated `admin:sso:*` permissions enforced by sf-auth-service
  // (`src/routes/ssoConfig.ts` requires `admin:sso:read` / `admin:sso:write`).
  // The tab is hidden upfront when the user lacks `show` — we never
  // issue the list GET for unauthorised users (AC8).
  sso: {
    list: 'admin:sso:read',
    show: 'admin:sso:read',
    create: 'admin:sso:write',
    edit: 'admin:sso:write',
    delete: 'admin:sso:*',
  },
  // SF-3733 — Eva platform overview + later Eva usage pages. Read-only,
  // gated by `admin:eva:usage:read` (matches sf-api requirePermissions on
  // /admin/eva/usage/*). Super admins inherit via the `*` / `admin:eva:*`
  // wildcards handled by hasPermission.
  eva: {
    list: 'admin:eva:usage:read',
    show: 'admin:eva:usage:read',
    create: null,
    edit: null,
    delete: null,
  },
  // SF-3800 — Eva "Overview" sider child (App.tsx) pointing at the /eva
  // overview page. Gated identically to the `eva` resource so the menu item
  // and its list access require `admin:eva:usage:read`, matching the page/APIs.
  'eva-overview': {
    list: 'admin:eva:usage:read',
    show: 'admin:eva:usage:read',
    create: null,
    edit: null,
    delete: null,
  },
  // SF-3839 — Eva "Platform Defaults" sider child (App.tsx) pointing at the
  // /eva/platform-defaults page. Global default Eva caps are an SF-internal
  // control, but access is gated on the PERMISSION `admin:eva:platform:read`
  // (read) / `admin:eva:platform:write` (write) — NOT the super-admin role —
  // matching sf-api requirePermissions on /admin/eva/budget-defaults. Super
  // admins inherit via the `admin:eva:*` / `*` wildcards in hasPermission, so
  // gating on the permission both hides the nav for users who lack it and keeps
  // the UI gate byte-identical to the server's enforcement (no role/permission
  // drift).
  'eva-platform-defaults': {
    list: 'admin:eva:platform:read',
    show: 'admin:eva:platform:read',
    create: 'admin:eva:platform:write',
    edit: 'admin:eva:platform:write',
    delete: null,
  },
  // SF-4762 — Platform feature flags page (/platform-flags). Same keys as
  // sf-api requirePermissions on /admin/platform-feature-flags, so the menu
  // hides for staff who lack read. Super admins pass via the `*` wildcard.
  'platform-flags': {
    list: 'admin:platform:flags:read',
    show: 'admin:platform:flags:read',
    create: 'admin:platform:flags:write',
    edit: 'admin:platform:flags:write',
    delete: null,
  },
  // SF-3736 — Eva "SF Internal" usage view (super-admin/internal Eva usage).
  // This is SF's own internal usage of Eva; it is gated on the Eva read
  // PERMISSION `admin:eva:usage:read` (matching sf-api requirePermissions on
  // /admin/eva/usage/superadmins*) — NOT the super-admin ROLE. Any internal
  // operator holding the Eva read permission (or the admin:eva:* / * wildcards)
  // can view it, so the nav entry and the page share the server's exact gate.
  // A dedicated permission (e.g. admin:eva:internal) can be split out later if
  // this view needs to diverge from the rest of the Eva read scope.
  'eva-superadmins': {
    list: 'admin:eva:usage:read',
    show: 'admin:eva:usage:read',
    create: null,
    edit: null,
    delete: null,
  },
  // SF-4651 — Demos (list + wizard + a build's page). Writes need the
  // `admin:demos:create` PERMISSION, the same gate as the sf-api
  // /admin/demo-builds write routes. SF-4746 — list and show also open with
  // `admin:demos:read_all` alone (sf-api lets it read GET /, /:id and
  // /:id/messages), so the menu shows for either permission.
  demos: {
    list: ['admin:demos:create', 'admin:demos:read_all'],
    show: ['admin:demos:create', 'admin:demos:read_all'],
    create: 'admin:demos:create',
    edit: 'admin:demos:create',
    delete: 'admin:demos:create',
  },
  // SF-4758 — Priorities. Open to every admin app user on purpose: a voter is anyone with a team
  // on their sign-in account (not a permission), so sf-api decides via GET /admin/v1/priorities/me
  // and the page explains when the caller cannot take part.
  priorities: { list: null, show: null, create: null, edit: null, delete: null },
  // SF-4845: Copy environment (/eng/env-copy). Every action needs `eng:env-copy`, the
  // permission sf-api requires on every /admin/env-copy route. Super admins pass via `*`.
  'env-copy': {
    list: 'eng:env-copy',
    show: 'eng:env-copy',
    create: 'eng:env-copy',
    edit: 'eng:env-copy',
    delete: 'eng:env-copy',
  },
  // Migrations (/eng/migrations). Every action needs `eng:migrations`, the permission sf-api
  // requires on every /admin/eng/migrations route. Super admins pass via `*`; `eng:*` covers both.
  'eng-migrations': {
    list: 'eng:migrations',
    show: 'eng:migrations',
    create: 'eng:migrations',
    edit: 'eng:migrations',
    delete: 'eng:migrations',
  },
  // The Engineering menu group (src/pages/eng/menu.tsx) has no page of its own. The sider shows
  // it only when `list` is allowed here, so it opens for either child's permission.
  eng: {
    list: ['eng:env-copy', 'eng:migrations'],
    show: ['eng:env-copy', 'eng:migrations'],
    create: ['eng:env-copy', 'eng:migrations'],
    edit: ['eng:env-copy', 'eng:migrations'],
    delete: ['eng:env-copy', 'eng:migrations'],
  },
};

/**
 * Returns the required permission (or any-of list) for a given resource/action pair.
 * Returns null if the action is always allowed (e.g. dashboard).
 *
 * @param resource - Refine resource name
 * @param action - Refine action (list, show, create, edit, delete)
 * @returns permission key, list of keys any one of which is enough, or null
 */
function getRequiredPermission(resource?: string, action?: string): RequiredPermission {
  if (!resource || !action) return null;
  return PERMISSION_MAP[resource]?.[action] ?? null;
}

/**
 * Refine access control provider.
 * Maps resource/action pairs to ServeFirst permissions and evaluates them
 * using client-side wildcard matching (mirrors auth-service logic).
 */
export const accessControlProvider: AccessControlProvider = {
  can: async ({ resource, action }: CanParams): Promise<CanReturnType> => {
    const requiredPermission = getRequiredPermission(resource, action);

    if (!requiredPermission) {
      return { can: true };
    }

    const anyOf = Array.isArray(requiredPermission) ? requiredPermission : [requiredPermission];
    const userPermissions = await getCachedPermissions();
    const allowed = anyOf.some((required) => hasPermission(userPermissions, required));

    return {
      can: allowed,
      ...(allowed ? {} : { reason: `Missing permission: ${anyOf.join(' or ')}` }),
    };
  },
};
